// User menu (avatar in the top bar): profile, password, sessions, « Connecteurs » (the office's
// connector library, for everyone while the server reports features.connectorLibrary; the count
// of the connectors the person may fix that wait for it), « Mon accès Claude (MCP) »
// (features.mcpPersonal: a member's own MCP keys; an administrator goes to Administration → Accès
// Claude (MCP)), theme, shortcuts, admin entries, logout. Also the profile (with the office
// connectors the person may have the agents use) and sessions dialogs.
import { h, badge, button, emptyState, spinner, icon } from '../dom.js';
import { t } from '../i18n/fr.js';
import { formatDateTime, relativeTime } from '../format.js';
import { pendingSuggestionCount } from '../store.js';
import { needsAttention, libraryAccess } from './myconnectors/model.js';
import { normalizePolicy, policyNames } from './connectorpolicy.js';
import { openMyMcpAccess } from './mcpaccess.js';
import { openPasswordModal } from './login.js';

/**
 * Opens « Mon accès Claude (MCP) »: a member's own keys; an administrator's keys are in
 * Administration → Accès Claude (MCP).
 * @param {object} ctx
 */
export function openMcpAccess(ctx) {
  if (ctx.store.get().me?.role === 'admin') return ctx.navigate('#/admin/mcp');
  return openMyMcpAccess(ctx);
}

/**
 * Short human description of a user agent: "Safari · Mac", "Chrome · Android"… (pure).
 * @param {string} ua
 */
export function describeUserAgent(ua) {
  const s = String(ua || '');
  if (!s) return t('sessions.unknownDevice');
  let device = '';
  if (/iPhone/i.test(s)) device = t('device.iphone');
  else if (/iPad/i.test(s)) device = t('device.ipad');
  else if (/Android/i.test(s)) device = t('device.android');
  else if (/Mac OS X|Macintosh/i.test(s)) device = t('device.mac');
  else if (/Windows/i.test(s)) device = t('device.windows');
  else if (/Linux/i.test(s)) device = t('device.linux');
  let browser = '';
  if (/Edg\//.test(s)) browser = t('browser.edge');
  else if (/Firefox\/|FxiOS/.test(s)) browser = t('browser.firefox');
  else if (/Chrome\/|CriOS/.test(s)) browser = t('browser.chrome');
  else if (/Safari\//.test(s)) browser = t('browser.safari');
  const parts = [browser, device].filter(Boolean);
  return parts.length ? parts.join(' · ') : t('sessions.unknownDevice');
}

/**
 * Opens the user menu anchored to the avatar button.
 * @param {object} ctx app context
 * @param {HTMLElement} anchor
 * @param {{onToggleTheme: Function, onLogout: Function, onShortcuts: Function}} actions
 */
export function openUserMenu(ctx, anchor, actions) {
  const state = ctx.store.get();
  const { me, features, ui, suggestions, libraryConnectors } = state;
  const isAdmin = me?.role === 'admin';
  // Admins: the suggestions waiting for their decision (the badge of the top-bar button, hidden on phones).
  const pending = isAdmin ? pendingSuggestionCount(suggestions) : 0;
  // The office's connectors: the entry for everyone while the library is on; the count of those
  // the person may fix (to reconnect, or in error).
  const access = libraryAccess(state);
  const library = access.show;
  const needs = library ? needsAttention(libraryConnectors) : 0;
  const dark = ui.theme === 'dark';
  const name = me?.displayName || me?.username || '';
  const head = h('div', { class: 'um-head' },
    h('p', { class: 'um-name' }, name),
    h('p', { class: 'um-user mono-label' }, `@${me?.username || ''}`),
    badge(t(isAdmin ? 'role.admin' : 'role.member'), isAdmin ? 'dark' : 'neutral'));
  const items = [
    { header: head },
    { label: t('menu.profile'), icon: 'user', onSelect: () => openProfile(ctx) },
    { label: t('menu.password'), icon: 'key', onSelect: () => openPasswordModal(ctx) },
    { label: t('menu.sessions'), icon: 'monitor', onSelect: () => openSessions(ctx) },
    library && { label: t('menu.connectors'), icon: 'layers', hint: needs ? String(needs) : undefined, onSelect: () => ctx.navigate('#/connectors') },
    features?.mcpPersonal === true && { label: t('menu.mcp'), icon: 'key', onSelect: () => openMcpAccess(ctx) },
    { separator: true },
    { label: t('menu.calendar'), icon: 'calendar', hint: 'P', onSelect: () => ctx.navigate('#/calendar') },
    { label: t('menu.board'), icon: 'columns', hint: 'T', onSelect: () => ctx.navigate('#/taches') },
    { label: t('menu.automations'), icon: 'repeat', onSelect: () => ctx.navigate('#/automations') },
    { label: t('menu.suggestions'), icon: 'lightbulb', hint: pending ? String(pending) : undefined, onSelect: () => ctx.navigate('#/suggestions') },
    { label: dark ? t('menu.themeLight') : t('menu.themeDark'), icon: dark ? 'sun' : 'moon', hint: 'D', onSelect: actions.onToggleTheme },
    { label: t('menu.shortcuts'), icon: 'keyboard', hint: '?', onSelect: actions.onShortcuts },
    isAdmin && { separator: true },
    isAdmin && { label: t('menu.admin'), icon: 'settings', onSelect: () => ctx.navigate('#/admin') },
    isAdmin && features?.atelier && { label: t('menu.atelier'), icon: 'hammer', onSelect: () => ctx.navigate('#/atelier') },
    { separator: true },
    { label: t('menu.logout'), icon: 'logout', danger: true, onSelect: actions.onLogout },
  ];
  return ctx.overlay.menu(anchor, items, { placement: 'bottom-end', label: t('topbar.userMenu', { name }), className: 'user-menu' });
}

/**
 * The text of the profile's « Connecteurs du bureau »: all for an administrator; for a member his
 * « Connecteurs autorisés », named (pure).
 * @param {{role?: string, connectorPolicy?: object}} me
 * @param {Array<{key: string, name?: string}>} [servers] to name the keys (bootstrap chips)
 * @returns {string}
 */
export function profileConnectorsText(me, servers = []) {
  if (me?.role === 'admin') return t('profile.connectorsAdmin');
  const p = normalizePolicy(me?.connectorPolicy);
  if (p.mode === 'all') return t('profile.connectorsAll');
  const list = policyNames(p, servers).join(', ');
  if (p.mode === 'only' && !list) return t('profile.connectorsNone');
  return t(p.mode === 'except' ? 'profile.connectorsExcept' : 'profile.connectorsOnly', { list });
}

/** Profile dialog (read-only): identity, role, dates, access grants and office connectors. */
export function openProfile(ctx) {
  const s = ctx.store.get();
  const me = s.me || {};
  const isAdmin = me.role === 'admin';
  const floorName = (id) => s.office.floors.find((f) => f.id === id)?.name || id;
  const deptName = (id) => (id === '*' ? t('profile.allDepts') : s.office.departments.find((d) => d.id === id)?.name || id);
  const row = (label, value) => h('div', { class: 'dl-row' }, h('dt', { class: 'mono-label' }, label), h('dd', {}, value || '—'));
  const grants = isAdmin
    ? h('p', { class: 'muted' }, t('profile.allFloors'))
    : (me.grants || []).length
      ? h('ul', { class: 'grant-list' }, (me.grants || []).map((g) => h('li', { class: 'grant-item' },
        h('span', {}, t('profile.grantLine', { floor: floorName(g.floorId), dept: deptName(g.deptId) })),
        badge(t(`level.${g.level}`), 'neutral'))))
      : h('p', { class: 'muted' }, t('profile.noGrants'));
  const content = h('div', { class: 'stack' },
    h('dl', { class: 'dl' },
      row(t('profile.displayName'), me.displayName),
      row(t('profile.username'), me.username),
      row(t('profile.role'), t(isAdmin ? 'role.admin' : 'role.member')),
      row(t('profile.createdAt'), me.createdAt ? formatDateTime(me.createdAt) : ''),
      row(t('profile.lastLogin'), me.lastLoginAt ? formatDateTime(me.lastLoginAt) : '')),
    h('h3', { class: 'section-title mono-label' }, t('profile.grants')),
    grants,
    h('h3', { class: 'section-title mono-label' }, t('profile.connectors')),
    h('p', { class: 'profile-connectors' }, profileConnectorsText(me, s.connectors || [])),
    isAdmin ? null : h('p', { class: 'muted profile-connectors-hint' }, t('profile.connectorsHint')),
    s.features?.mcpPersonal === true ? h('h3', { class: 'section-title mono-label' }, t('profile.mcp')) : null,
    s.features?.mcpPersonal === true ? h('div', { class: 'profile-mcp' },
      h('p', { class: 'muted' }, t('profile.mcpText')),
      button(t('profile.mcpOpen'), {
        variant: 'secondary', size: 'sm', icon: 'key', class: 'profile-mcp-open',
        onClick: () => { layer?.close?.('api'); openMcpAccess(ctx); },
      })) : null);
  const layer = ctx.overlay.openModal({ title: t('profile.title'), size: 'sm', content });
  return layer;
}

/** Sessions dialog: lists the user's sessions with revoke buttons. */
export function openSessions(ctx) {
  const list = h('div', { class: 'session-list' }, spinner());
  const layer = ctx.overlay.openModal({ title: t('sessions.title'), subtitle: t('sessions.subtitle'), size: 'md', content: list });

  async function load() {
    try {
      const sessions = await ctx.api.get('/api/me/sessions');
      render(Array.isArray(sessions) ? sessions : []);
    } catch (err) {
      list.replaceChildren(emptyState({ icon: 'alert', title: t('sessions.unavailable'), text: err.message }));
    }
  }

  function render(sessions) {
    if (!sessions.length) { list.replaceChildren(emptyState({ icon: 'monitor', title: t('sessions.empty') })); return; }
    const time = (v) => new Date(v || 0).getTime() || 0;
    const sorted = [...sessions].sort((a, b) => Number(!!b.current) - Number(!!a.current) || time(b.lastSeenAt) - time(a.lastSeenAt));
    list.replaceChildren(...sorted.map((sess) => {
      const revoke = sess.current ? badge(t('sessions.current'), 'success') : button(t('sessions.revoke'), {
        variant: 'ghost', size: 'sm',
        onClick: async () => {
          const ok = await ctx.overlay.confirm({ title: t('sessions.confirmTitle'), message: t('sessions.confirmText'), confirmLabel: t('sessions.revoke'), danger: true });
          if (!ok) return;
          try {
            await ctx.api.del(`/api/me/sessions/${encodeURIComponent(sess.id)}`);
            ctx.toast.success(t('sessions.revoked'));
            load();
          } catch (err) {
            ctx.toast.error(err);
          }
        },
      });
      return h('div', { class: ['session-row', { 'is-current': sess.current }] },
        h('span', { class: 'session-icon' }, icon(/iPhone|Android|iPad|Mobile/i.test(sess.ua || '') ? 'phone' : 'monitor', { size: 18 })),
        h('div', { class: 'session-main' },
          h('p', { class: 'session-device' }, describeUserAgent(sess.ua)),
          h('p', { class: 'session-meta' }, [sess.ip, sess.lastSeenAt ? t('sessions.lastSeen', { when: relativeTime(sess.lastSeenAt) }) : '',
            sess.createdAt ? t('sessions.created', { when: relativeTime(sess.createdAt) }) : ''].filter(Boolean).join(' · '))),
        revoke);
    }));
  }

  load();
  return layer;
}
